import notificationError from "../../../system-components/toasters/notificationError";
import { dollarToCents } from "./configureCurrencyToCents";
import onlyUnique from "../../filters/filterOnlyUnique";
import { checkUndefinedOrNull } from "../checkers/checkUndefined";
import { INV_BUNDLE_ITEM } from "../modelConstants/modelConstants";
import {
  InventoryItem,
  InventoryItemFormData,
} from "../../../interfaces/inventoryItem";

export const configureInventory = () => {
  const convertRates = (
    rates: {
      rate: number;
      rateName: string;
      rateRange: string;
      id: string;
    }[]
  ) => {
    if (typeof rates === "undefined") return [];
    if (rates.length === 0) return [];
    return rates.map((r) => {
      const { rate, ...rest } = r;
      return {
        rate: dollarToCents(rate),
        ...rest,
      };
    });
  };

  const determineInheritTurnaround = (
    type: string | undefined,
    turnaround: any
  ) => {
    const t = checkUndefinedOrNull(type, "rental");
    const turn = checkUndefinedOrNull(turnaround, 0);
    return t === INV_BUNDLE_ITEM && turn !== 0;
  };

  function newInventoryItem(
    values: Partial<InventoryItemFormData>,
    createdById: string,
    orgData: any
  ) {
    if (!values || !createdById || !orgData)
      return notificationError("Could not create item", "");
    const newItem: Partial<InventoryItem> = {
      orgId: orgData.id,
      type: values.type ? values.type : "rental",
      name: values.name ?? "",
      description: values.description ?? "",
      stock: values.stock ? values.stock : null,
      turnaround: values.turnaround ? values.turnaround : null,
      inheritParentTurnaround: determineInheritTurnaround(
        values.type,
        values.turnaround
      ),
      categories: values.categories ? values.categories : [],
      categoriesQueryHook: values.categoriesQueryHook
        ? values.categoriesQueryHook
        : [],
      rates: convertRates(values.rates ?? []),
      showInShop: values.showInShop || null,
      embeddedShopRateId: values.embeddedShopRateId || null,
      depreciation: {
        initialValue: values.initialValue ? values.initialValue : null,
        initialDate: values.initialDate ? values.initialDate : null,
        depreciationYears: values.depreciationYears
          ? values.depreciationYears
          : null,
      },
      notes: values.notes ? values.notes : "",
      image: values.image ? values.image : null,
      isAvailable: values.isAvailable ?? false,
      isActive: values.isActive ?? false,
      isTaxable: values.isTaxable ?? false,
      hasMaintenanceDetails: values.hasMaintenanceDetails ?? false,
      bundleItems: values.bundleItems ? values.bundleItems : [],
      bundleItemsQueryHook: values.bundleItemsQueryHook
        ? values.bundleItemsQueryHook.filter(onlyUnique)
        : [],
      createdOn: new Date(),
      createdBy: createdById,
      updatedOn: new Date(),
      updatedBy: createdById,
      updateSource: "adelie",
      createdOnPlatform: "adelie",
    };
    return newItem;
  }
  function editInventoryItem(
    values: InventoryItemFormData,
    editedById: string,
    orgData: any
  ): Partial<InventoryItem> | undefined {
    if (!values || !editedById || !orgData) {
      notificationError("Could not edit item", ""); // could we throw an exception here?
      return undefined;
    }

    const hasMaintenanceDetails = values.hasMaintenanceDetails ?? false
    let maintenanceDetails = null;
    if (hasMaintenanceDetails) {
      maintenanceDetails = {
        activeHours: values.activeHours ? values.activeHours : 0,
        hoursBetweenServices: values.hoursBetweenServices ? values.hoursBetweenServices : 0,
        timeBetweenServices: null,
        maintenanceNotes: null,
      }
    }

    const result: Partial<InventoryItem> = {
      orgId: orgData.id,
      type: values.type ? values.type : "rental",
      name: values.name,
      description: values.description,
      stock: values.stock ? values.stock : null,
      turnaround: values.turnaround ? values.turnaround : null,
      categories: values.categories ? values.categories : [],
      categoriesQueryHook: values.categoriesQueryHook
        ? values.categoriesQueryHook
        : [], // for referencing categories
      rates: convertRates(values.rates),
      showInShop: values.showInShop || false,
      embeddedShopRateId: values.embeddedShopRateId || null,
      depreciation: {
        initialValue: values.initialValue ? values.initialValue : null,
        initialDate: values.initialDate ? values.initialDate : null,
        depreciationYears: values.depreciationYears
          ? values.depreciationYears
          : null,
      },
      inheritParentTurnaround: determineInheritTurnaround(
        values.type,
        values.turnaround
      ),
      notes: values.notes ? values.notes : "",
      image: values.image ? values.image : null, // object with url and name
      isAvailable: values.isAvailable, // populates in searching available inventory
      isActive: values.isActive, // denotes temporary holds (ie servicing etc)
      isTaxable: values.isTaxable,
      hasMaintenanceDetails,
      maintenanceDetails,
      bundleItems: values.bundleItems ? values.bundleItems : [],
      bundleItemsQueryHook: values.bundleItemsQueryHook
        ? values.bundleItemsQueryHook.filter(onlyUnique)
        : [],
      updatedOn: new Date(),
      updatedBy: editedById,
      updateSource: "adelie",
    };
    return result;
  }

  function duplicateInventoryItem(
    values: any,
    createdById: string,
    orgData: any
  ) {
    if (!values || !createdById || !orgData)
      return notificationError("Could not edit item", "");
    const {
      id,
      name,
      createdOn,
      createdBy,
      updatedOn,
      updatedBy,
      rates,

      /**
       * Remove from configuring
       */
      qbId,
      updateSource,
      createdOnPlatform,
      /**
       *
       */
      ...rest
    } = values;
    return {
      name: `${name} Copy`,
      createdOn: new Date(),
      createdBy: createdById,
      updatedOn: new Date(),
      updatedBy: createdById,
      rates: convertRates(rates),
      updateSource: "adelie",
      createdOnPlatform: "adelie",
      ...rest,
    };
  }

  return {
    newInventoryItem, // configure new inventory item
    editInventoryItem, // edit inventory item
    duplicateInventoryItem, // duplicate inventory item
  };
};
